import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { apiRequest } from "@/shared/utils/queryClient";
import { useToast } from "@/shared/hooks/use-toast";
// Quote form uses a standalone Zod schema (insertQuoteSchema removed — was unused)
// CR-3 Stage-3A: dead import eliminated

// Create a simplified schema that only validates the visible form fields
const quoteFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Valid email is required"),
  phone: z.string().min(1, "Phone number is required"),
  postcode: z.string().min(1, "Postcode is required"),
  propertyType: z.enum(["residential", "commercial", "industrial"]),
  propertySize: z.enum(["small", "medium", "large"]),
  pestTypes: z.array(z.string()).min(1, "Please select at least one pest type"),
  description: z.string().optional(),
});

type QuoteFormData = z.infer<typeof quoteFormSchema>;

interface QuoteRequestFormProps {
  onClose: () => void;
}

export default function QuoteRequestForm({ onClose }: QuoteRequestFormProps) {
  const [selectedPests, setSelectedPests] = useState<string[]>([]);
  const { toast } = useToast();

  const form = useForm<QuoteFormData>({
    resolver: zodResolver(quoteFormSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      postcode: "",
      propertyType: "residential",
      propertySize: "medium",
      pestTypes: [],
      description: "",
    },
  });

  const quoteMutation = useMutation({
    mutationFn: async (data: QuoteFormData) => {
      const response = await apiRequest("POST", "/api/quotes", data);
      return await response.json(); // Parse JSON from Response object
    },
    onSuccess: () => {
      toast({
        title: "Quote request submitted!",
        description:
          "We will contact you within 24 hours with your personalized quote.",
      });
      onClose();
    },
    onError: (error: any) => {
      console.error("Quote submission error:", error); // Debug logging
      toast({
        title: "Error submitting quote request",
        description: "Please try again or call us directly.",
        variant: "destructive",
      });
    },
  });

  const pestTypes = [
    "ants",
    "rodents",
    "cockroaches",
    "birds",
    "bed-bugs",
    "wasps",
    "flies",
    "moths",
  ];

  const handlePestToggle = (pest: string) => {
    const newSelected = selectedPests.includes(pest)
      ? selectedPests.filter((p) => p !== pest)
      : [...selectedPests, pest];

    setSelectedPests(newSelected);
    form.setValue("pestTypes", newSelected);
  };

  const onSubmit = (data: QuoteFormData) => {
    // Add required fields that are missing from the form but required by the database
    const completeData = {
      ...data,
      address: `${data.postcode}`, // Use postcode as basic address for now
      severity: "medium", // Default severity level
      urgency: "normal", // Default urgency level
      serviceType: "general-pest-control", // Default service type
      contactMethod: "phone", // Default contact method
    };

    quoteMutation.mutate(completeData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Request a Quote</h2>
          <button onClick={onClose}>
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Property Type
              </label>
              <select
                {...form.register("propertyType")}
                data-testid="select-property-type"
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none"
              >
                <option value="residential">Residential</option>
                <option value="commercial">Commercial</option>
                <option value="industrial">Industrial</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Property Size
              </label>
              <select
                {...form.register("propertySize")}
                data-testid="select-property-size"
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none"
              >
                <option value="small">Small (&lt; 1,500 sq ft)</option>
                <option value="medium">Medium (1,500-3,000 sq ft)</option>
                <option value="large">Large (&gt; 3,000 sq ft)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Pest Problem
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {pestTypes.map((pest) => (
                <label
                  key={pest}
                  className="flex items-center space-x-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedPests.includes(pest)}
                    onChange={() => handlePestToggle(pest)}
                    data-testid={`checkbox-pest-${pest}`}
                    className="text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm capitalize">
                    {pest.replace("-", " ")}
                  </span>
                </label>
              ))}
            </div>
            {form.formState.errors.pestTypes && (
              <p className="text-red-500 text-sm mt-1">
                {form.formState.errors.pestTypes.message}
              </p>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <input
              {...form.register("name")}
              type="text"
              placeholder="Full Name"
              data-testid="input-name"
              className="border border-gray-300 rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none"
            />
            <input
              {...form.register("email")}
              type="email"
              placeholder="Email Address"
              data-testid="input-email"
              className="border border-gray-300 rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <input
              {...form.register("phone")}
              type="tel"
              placeholder="Phone Number"
              data-testid="input-phone"
              className="border border-gray-300 rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none"
            />
            <input
              {...form.register("postcode")}
              type="text"
              placeholder="Postcode"
              data-testid="input-postcode"
              className="border border-gray-300 rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none"
            />
          </div>

          <textarea
            {...form.register("description")}
            rows={3}
            placeholder="Additional details about your pest problem..."
            data-testid="textarea-description"
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-emerald-500 focus:outline-none"
          />

          {/* Form validation errors debug display - safe serialization */}
          {Object.keys(form.formState.errors).length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-red-800 font-semibold mb-2">
                Form Validation Errors:
              </h3>
              <ul className="text-red-600 text-sm space-y-1">
                {Object.entries(form.formState.errors).map(([field, error]) => (
                  <li key={field}>
                    <strong>{field}:</strong> {error?.message || "Invalid"}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end space-x-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={quoteMutation.isPending}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="button-get-quote"
            >
              {quoteMutation.isPending ? "Submitting..." : "Get Quote"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
